import { useState, useEffect } from 'react';
import { X, Calendar, MapPin, AlignLeft, Repeat, Trash2 } from 'lucide-react';
import type {
  CalendarEvent,
  CalendarEventType,
  RecurrenceType,
  CreateCalendarEventInput,
} from '../../types/calendar';
import {
  CALENDAR_EVENT_TYPE_LABELS,
  RECURRENCE_TYPE_LABELS,
  WEEKDAY_LABELS,
} from '../../types/calendar';
import { logger } from '../../lib/logger';

interface CalendarEventModalProps {
  isOpen: boolean;
  event: CalendarEvent | null;
  defaultDate?: Date;
  onClose: () => void;
  onSave: (input: CreateCalendarEventInput) => Promise<void>;
  onDelete?: (eventId: string) => Promise<void>;
}

export function CalendarEventModal({
  isOpen,
  event,
  defaultDate,
  onClose,
  onSave,
  onDelete,
}: CalendarEventModalProps) {
  const [title, setTitle] = useState('');
  const [type, setType] = useState<CalendarEventType>('event');
  const [description, setDescription] = useState('');
  const [location, setLocation] = useState('');
  const [allDay, setAllDay] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [startTime, setStartTime] = useState('09:00');
  const [endDate, setEndDate] = useState('');
  const [endTime, setEndTime] = useState('10:00');
  const [recurrence, setRecurrence] = useState<RecurrenceType>('none');
  const [recurrenceInterval, setRecurrenceInterval] = useState(1);
  const [recurrenceDaysOfWeek, setRecurrenceDaysOfWeek] = useState<number[]>([]);
  const [recurrenceUntil, setRecurrenceUntil] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (event) {
      setTitle(event.title);
      setType(event.type);
      setDescription(event.description || '');
      setLocation(event.location || '');
      setAllDay(event.all_day);

      const start = new Date(event.start_at);
      const end = new Date(event.end_at);
      setStartDate(start.toISOString().split('T')[0]);
      setStartTime(start.toTimeString().slice(0, 5));
      setEndDate(end.toISOString().split('T')[0]);
      setEndTime(end.toTimeString().slice(0, 5));

      setRecurrence(event.recurrence);
      setRecurrenceInterval(event.recurrence_interval || 1);
      setRecurrenceDaysOfWeek(event.recurrence_days_of_week || []);
      setRecurrenceUntil(event.recurrence_until || '');
    } else {
      // Reset to defaults
      const date = defaultDate || new Date();
      const dateStr = date.toISOString().split('T')[0];
      setTitle('');
      setType('event');
      setDescription('');
      setLocation('');
      setAllDay(false);
      setStartDate(dateStr);
      setStartTime('09:00');
      setEndDate(dateStr);
      setEndTime('10:00');
      setRecurrence('none');
      setRecurrenceInterval(1);
      setRecurrenceDaysOfWeek([]);
      setRecurrenceUntil('');
    }
  }, [event, defaultDate, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;

    setIsSaving(true);
    try {
      const startAt = allDay
        ? `${startDate}T00:00:00`
        : `${startDate}T${startTime}:00`;
      const endAt = allDay
        ? `${endDate}T23:59:59`
        : `${endDate}T${endTime}:00`;

      await onSave({
        title: title.trim(),
        type,
        description: description.trim() || null,
        location: location.trim() || null,
        all_day: allDay,
        start_at: new Date(startAt).toISOString(),
        end_at: new Date(endAt).toISOString(),
        recurrence,
        recurrence_interval: recurrence !== 'none' ? recurrenceInterval : undefined,
        recurrence_days_of_week: recurrence === 'weekly' && recurrenceDaysOfWeek.length > 0
          ? recurrenceDaysOfWeek
          : undefined,
        recurrence_until: recurrenceUntil || null,
      });

      onClose();
    } catch (error) {
      logger.error('Failed to save event:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!event || !onDelete) return;
    if (!confirm('Möchtest du diesen Termin wirklich löschen?')) return;

    setIsSaving(true);
    try {
      await onDelete(event.id);
      onClose();
    } catch (error) {
      logger.error('Failed to delete event:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const toggleDayOfWeek = (day: number) => {
    setRecurrenceDaysOfWeek((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      <div className="relative bg-white dark:bg-slate-800 rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-100 dark:border-slate-700 flex-shrink-0">
          <h2 className="text-lg font-semibold text-[#1A1A1A] dark:text-white">
            {event ? 'Termin bearbeiten' : 'Neuer Termin'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-slate-300 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Title */}
          <div>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Titel hinzufügen"
              className="w-full px-4 py-3 text-lg font-medium border-0 border-b-2 border-gray-200 dark:border-slate-600 bg-transparent text-[#1A1A1A] dark:text-white placeholder-gray-400 dark:placeholder-slate-500 focus:border-primary focus:outline-none"
              autoFocus
            />
          </div>

          {/* Type */}
          <div>
            <label className="block text-sm font-medium text-[#6B6B6B] dark:text-slate-400 mb-2">Art</label>
            <div className="flex flex-wrap gap-2">
              {(Object.keys(CALENDAR_EVENT_TYPE_LABELS) as CalendarEventType[]).map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setType(t)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    type === t
                      ? 'bg-primary text-white'
                      : 'bg-gray-100 dark:bg-slate-700 text-[#6B6B6B] dark:text-slate-300 hover:bg-gray-200 dark:hover:bg-slate-600'
                  }`}
                >
                  {CALENDAR_EVENT_TYPE_LABELS[t]}
                </button>
              ))}
            </div>
          </div>

          {/* All Day Toggle */}
          <div className="flex items-center gap-3">
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={allDay}
                onChange={(e) => setAllDay(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 dark:bg-slate-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 dark:after:border-slate-500 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
            </label>
            <span className="text-sm text-[#1A1A1A] dark:text-white">Ganztägig</span>
          </div>

          {/* Date/Time */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-[#6B6B6B] dark:text-slate-400 mb-1">
                <Calendar className="w-4 h-4 inline mr-1" />
                Start
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  if (e.target.value > endDate) {
                    setEndDate(e.target.value);
                  }
                }}
                className="w-full px-3 py-2 border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-[#1A1A1A] dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
              {!allDay && (
                <input
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  className="w-full mt-2 px-3 py-2 border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-[#1A1A1A] dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-[#6B6B6B] dark:text-slate-400 mb-1">Ende</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                min={startDate}
                className="w-full px-3 py-2 border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-[#1A1A1A] dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
              {!allDay && (
                <input
                  type="time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  className="w-full mt-2 px-3 py-2 border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-[#1A1A1A] dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
              )}
            </div>
          </div>

          {/* Location */}
          <div>
            <label className="block text-sm font-medium text-[#6B6B6B] dark:text-slate-400 mb-1">
              <MapPin className="w-4 h-4 inline mr-1" />
              Ort (optional)
            </label>
            <input
              type="text"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="Ort hinzufügen"
              className="w-full px-3 py-2 border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-[#1A1A1A] dark:text-white placeholder-gray-400 dark:placeholder-slate-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-[#6B6B6B] dark:text-slate-400 mb-1">
              <AlignLeft className="w-4 h-4 inline mr-1" />
              Beschreibung (optional)
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Beschreibung hinzufügen"
              rows={3}
              className="w-full px-3 py-2 border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-[#1A1A1A] dark:text-white placeholder-gray-400 dark:placeholder-slate-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary resize-none"
            />
          </div>

          {/* Recurrence */}
          <div>
            <label className="block text-sm font-medium text-[#6B6B6B] dark:text-slate-400 mb-2">
              <Repeat className="w-4 h-4 inline mr-1" />
              Wiederholung
            </label>
            <select
              value={recurrence}
              onChange={(e) => setRecurrence(e.target.value as RecurrenceType)}
              className="w-full px-3 py-2 border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-[#1A1A1A] dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {(Object.keys(RECURRENCE_TYPE_LABELS) as RecurrenceType[]).map((r) => (
                <option key={r} value={r}>
                  {RECURRENCE_TYPE_LABELS[r]}
                </option>
              ))}
            </select>

            {recurrence !== 'none' && (
              <div className="mt-3 space-y-3 p-3 bg-gray-50 dark:bg-slate-700 rounded-lg">
                {/* Interval */}
                <div className="flex items-center gap-2">
                  <span className="text-sm text-[#6B6B6B] dark:text-slate-400">Alle</span>
                  <input
                    type="number"
                    min="1"
                    max="99"
                    value={recurrenceInterval}
                    onChange={(e) => setRecurrenceInterval(parseInt(e.target.value) || 1)}
                    className="w-16 px-2 py-1 border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-600 text-[#1A1A1A] dark:text-white rounded text-center"
                  />
                  <span className="text-sm text-[#6B6B6B] dark:text-slate-400">
                    {recurrence === 'daily' && (recurrenceInterval === 1 ? 'Tag' : 'Tage')}
                    {recurrence === 'weekly' && (recurrenceInterval === 1 ? 'Woche' : 'Wochen')}
                    {recurrence === 'monthly' && (recurrenceInterval === 1 ? 'Monat' : 'Monate')}
                    {recurrence === 'yearly' && (recurrenceInterval === 1 ? 'Jahr' : 'Jahre')}
                  </span>
                </div>

                {/* Days of week for weekly */}
                {recurrence === 'weekly' && (
                  <div>
                    <span className="text-sm text-[#6B6B6B] dark:text-slate-400 block mb-2">An diesen Tagen:</span>
                    <div className="flex gap-1">
                      {[1, 2, 3, 4, 5, 6, 0].map((day) => (
                        <button
                          key={day}
                          type="button"
                          onClick={() => toggleDayOfWeek(day)}
                          className={`w-9 h-9 rounded-full text-sm font-medium transition-colors ${
                            recurrenceDaysOfWeek.includes(day)
                              ? 'bg-primary text-white'
                              : 'bg-white dark:bg-slate-600 border border-gray-200 dark:border-slate-500 text-[#6B6B6B] dark:text-slate-300 hover:border-primary'
                          }`}
                        >
                          {WEEKDAY_LABELS[day]}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* End date */}
                <div>
                  <label className="text-sm text-[#6B6B6B] dark:text-slate-400 block mb-1">Endet am (optional)</label>
                  <input
                    type="date"
                    value={recurrenceUntil}
                    onChange={(e) => setRecurrenceUntil(e.target.value)}
                    min={startDate}
                    className="w-full px-3 py-2 border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-600 text-[#1A1A1A] dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
              </div>
            )}
          </div>

        </div>

          {/* Actions - fixed footer */}
          <div className="flex items-center gap-3 p-4 border-t border-gray-100 dark:border-slate-700 flex-shrink-0">
            {event && onDelete && (
              <button
                type="button"
                onClick={handleDelete}
                disabled={isSaving}
                className="flex items-center gap-2 px-4 py-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors disabled:opacity-50"
              >
                <Trash2 className="w-4 h-4" />
                Löschen
              </button>
            )}
            <div className="flex-1" />
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-[#6B6B6B] dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={isSaving || !title.trim()}
              className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? 'Speichern...' : 'Speichern'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
